import {
  QueryConstraint,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter,
} from 'firebase/firestore'
import { keys, values } from 'lodash'
import { diagnosisTitles } from '../../../../types/FirestoreUser'
import { Filter } from '../types'

/**
 * Builds Firestore query constraints based on filter type and input
 * Handles search, list, role, diagnosis, and study filters
 *
 * @param filter - The selected filter type
 * @param input - Search input value (lowercased and trimmed)
 * @param studies - Available studies mapping (id -> name)
 * @param lastDoc - Last document for pagination (optional)
 * @param limitCount - Number of documents to fetch (default: 1000)
 * @returns Array of Firestore query constraints
 */
export function buildQueryConstraints(
  filter: Filter | undefined,
  input: string,
  studies: Record<string, string>,
  lastDoc?: any,
  limitCount: number = 1000
): QueryConstraint[] {
  const lc = input.toLowerCase().trim()
  const constraints: QueryConstraint[] = []

  // Build constraints based on filter type
  // Check for study filter FIRST before other conditions
  if (
    filter &&
    (values(studies).includes(filter as string) ||
      keys(studies).includes(filter as string))
  ) {
    // Study filter
    // CRITICAL: Firestore array-contains with objects only matches if the ENTIRE object matches exactly.
    // Since studies are stored as StudyData[] objects, we cannot query by nested fields.
    // WORKAROUND: Order by lastName and filter client-side in useUsersQuery
    constraints.push(orderBy('profile.searchLastName', 'asc'))
  } else if (filter === 'firstName') {
    constraints.push(
      where('profile.searchFirstName', '>=', lc),
      where('profile.searchFirstName', '<', lc + '~')
    )
  } else if (filter === 'lastName') {
    constraints.push(
      where('profile.searchLastName', '>=', lc),
      where('profile.searchLastName', '<', lc + '~')
    )
  } else if (filter === 'email') {
    constraints.push(where('email', '>=', lc), where('email', '<', lc + '~'))
  } else if (filter === 'patientId') {
    constraints.push(where('patientId', '==', lc.replace('#', '')))
  } else if (filter === 'alphaFirst') {
    constraints.push(orderBy('profile.searchFirstName', 'asc'))
  } else if (filter === 'alphaLast') {
    constraints.push(orderBy('profile.searchLastName', 'asc'))
  } else if (filter === 'alphaEmail') {
    constraints.push(orderBy('email', 'asc'))
  } else if (filter === 'admins') {
    constraints.push(
      where('roles', 'array-contains', 'admin'),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter === 'patients') {
    constraints.push(
      where('roles', 'array-contains', 'patient'),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter === 'superadmins') {
    constraints.push(
      where('roles', 'array-contains', 'superadmin'),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter === 'testers') {
    constraints.push(
      where('roles', 'array-contains', 'tester'),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter === 'researchers') {
    constraints.push(
      where('roles', 'array-contains', 'researcher'),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter && keys(diagnosisTitles).includes(filter)) {
    // Specific diagnosis
    constraints.push(
      where('profile.diagnosis', '==', filter),
      orderBy('profile.searchLastName', 'asc')
    )
  } else if (filter === 'selectDiagnoses') {
    // Multiple diagnoses
    constraints.push(
      where('profile.diagnosis', 'in', ['als', 'pls', 'als-probable']),
      orderBy('profile.searchLastName', 'asc')
    )
  }

  // Add pagination if lastDoc provided
  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }

  // Add limit
  constraints.push(firestoreLimit(limitCount))

  return constraints
}
